import React, { MouseEvent, TouchEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Center, CenterProps, Flex, FlexProps } from '@chakra-ui/react';

import { SEHomeCard, SEHomeCardProps } from './SEHomeCard';

type Props = {
  primaryButtonProps: CenterProps & { 'data-testid'?: string };
  secondaryButtonProps?: CenterProps & { 'data-testid'?: string };
  disableSlide?: boolean;
  containerProps?: FlexProps & { 'data-testid'?: string };
  // Use this prop to allow consumer to force the slider to close. Sort of hacky, but without lifting all the slider
  // state to the parent component (unnecessarily complex), this is the best way to do this I can see, aside from some
  // sort of observer pattern implementation (bit of a React anti-pattern).
  shouldForceResetSlide?: boolean;
  // Stable string key used to track whether the swipe hint has already been shown for this page.
  // Pass a constant (e.g. "notifications", "schedules"). If omitted, the hint is skipped.
  swipeHintKey?: string;
  // When true, tapping the card body replays the swipe hint animation as a reminder that the card is
  // swipeable. Opt-in so consumers that already use card taps for navigation (e.g. notifications) are unaffected.
  hintOnTap?: boolean;
  // Fires when the card transitions between open (buttons visible) and closed states.
  onOpenStateChange?: (isOpen: boolean) => void;
} & SEHomeCardProps;

// Module-level guard: prevents multiple list-item instances from all scheduling the hint simultaneously
// when they mount at the same time. Only the first instance per key runs; the rest skip.
const scheduledHintKeys = new Set<string>();

const MINIMUM_INITIAL_DRAG_THRESHOLD_PX = 20;

export function SwipeableSEHomeCard({
  children,
  primaryButtonProps,
  secondaryButtonProps,
  disableSlide = false,
  shouldForceResetSlide = false,
  swipeHintKey,
  hintOnTap = false,
  onOpenStateChange,
  containerProps,
  onClick,
  ...cardProps
}: Props) {
  const moveXCoords = useRef<{ start: number | null; current: number | null; isDragging: boolean; isOpen: boolean }>({
    start: null,
    current: null,
    isDragging: false,
    isOpen: false,
  });
  const buttonSetContainerRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const redBgRef = useRef<HTMLDivElement | null>(null);
  const greyBgRef = useRef<HTMLDivElement | null>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  // Tracks whether the current gesture moved far enough to count as a swipe, so a tap can be told apart from a drag.
  const wasDraggedRef = useRef(false);
  // Guards against overlapping hint animations (e.g. rapid taps) stacking style mutations on top of each other.
  const isHintPlayingRef = useRef(false);
  // Pending hint timers, cleared on unmount so callbacks don't fire against a torn-down card.
  const hintTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  function isRunningInCypress() {
    if (typeof window === 'undefined') return false;
    return Boolean((window as Window & { Cypress?: unknown }).Cypress);
  }

  useEffect(() => {
    if (shouldForceResetSlide) resetSlide(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldForceResetSlide]);

  // Spring-bounce the card left to reveal the action area, then back — hinting that the card is swipeable.
  // Used both for the one-time on-mount hint and (when hintOnTap is set) on every card tap.
  // onComplete fires once the animation has fully settled.
  const playSwipeHint = useCallback(
    (onComplete?: () => void) => {
      // Skip while sliding is disabled, an animation is already running, or the card is currently swiped open.
      if (disableSlide || isHintPlayingRef.current || moveXCoords.current.isOpen) return;

      const card = cardRef.current;
      const redBg = redBgRef.current;
      const greyBg = greyBgRef.current;
      const buttonContainer = buttonSetContainerRef.current;
      if (!card || !redBg || !greyBg || !buttonContainer) return;

      isHintPlayingRef.current = true;
      // Previous hint's timers have all fired by now (the guard above blocks re-entry until cleanup runs),
      // so it's safe to drop their stale ids and keep the array from growing across repeated taps. Mutate in
      // place rather than reassigning so the unmount cleanup keeps tracking the same array.
      hintTimersRef.current.length = 0;

      const PEEK_PX = 28;
      const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
      const SPRING_MS = 480;
      const HOLD_MS = 700;

      // Softly fade the action-area glow in as the card peeks out (rather than snapping it on)
      redBg.style.visibility = 'visible';
      greyBg.style.visibility = 'visible';
      redBg.style.transition = `opacity ${SPRING_MS}ms ease`;
      greyBg.style.transition = `opacity ${SPRING_MS}ms ease`;
      redBg.style.opacity = '1';
      greyBg.style.opacity = '1';
      buttonContainer.style.visibility = 'visible';
      buttonContainer.style.width = `${PEEK_PX}px`;
      card.style.transition = `transform ${SPRING_MS}ms ${SPRING}`;
      buttonContainer.style.transition = `width ${SPRING_MS}ms ${SPRING}`;
      card.style.transform = `translateX(-${PEEK_PX}px)`;

      const closeTimer = setTimeout(() => {
        if (!cardRef.current) return;
        card.style.transition = `transform ${SPRING_MS}ms ${SPRING}`;
        buttonContainer.style.transition = `width ${SPRING_MS}ms ${SPRING}`;
        // ...and fade the glow back out as the card slides home
        redBg.style.transition = `opacity ${SPRING_MS}ms ease`;
        greyBg.style.transition = `opacity ${SPRING_MS}ms ease`;
        redBg.style.opacity = '0';
        greyBg.style.opacity = '0';
        card.style.transform = 'translateX(0px)';
        buttonContainer.style.width = '0px';

        const cleanupTimer = setTimeout(() => {
          if (cardRef.current && !moveXCoords.current.isDragging) {
            card.style.transition = '';
            buttonContainer.style.transition = '';
            // Clear the bg opacity transitions too, otherwise the next real drag inherits them and the
            // action backgrounds fade in instead of snapping to the sharp, fully-opaque grey/red boundary.
            redBg.style.transition = '';
            redBg.style.visibility = 'hidden';
            redBg.style.opacity = '0';
            greyBg.style.transition = '';
            greyBg.style.visibility = 'hidden';
            greyBg.style.opacity = '0';
            buttonContainer.style.visibility = 'hidden';
          }
          isHintPlayingRef.current = false;
          onComplete?.();
        }, SPRING_MS);
        hintTimersRef.current.push(cleanupTimer);
      }, HOLD_MS);
      hintTimersRef.current.push(closeTimer);
    },
    [disableSlide]
  );

  // Clear any in-flight hint timers on unmount so their callbacks don't run against a torn-down card.
  useEffect(() => {
    const timers = hintTimersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  // One-time swipe hint on mount: replays the peek animation once per swipeHintKey (tracked in localStorage).
  useEffect(() => {
    // The onboarding hint animation is non-functional and can race swipe assertions in E2E tests.
    if (disableSlide || !swipeHintKey || isRunningInCypress()) return;

    const storageKey = `swipe-hint-seen:${swipeHintKey}`;
    // Skip if already seen (localStorage) or already scheduled this session (module-level guard)
    if (localStorage.getItem(storageKey) || scheduledHintKeys.has(swipeHintKey)) return;
    scheduledHintKeys.add(swipeHintKey);

    const INITIAL_DELAY_MS = 900;
    const openTimer = setTimeout(() => {
      // Mark seen only after the animation has fully completed
      playSwipeHint(() => localStorage.setItem(storageKey, '1'));
    }, INITIAL_DELAY_MS);

    return () => {
      clearTimeout(openTimer);
      // If unmounted before the animation completed, release the session guard so a remount can retry
      if (!localStorage.getItem(storageKey)) scheduledHintKeys.delete(swipeHintKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cardElement = cardRef.current!;
    // Force a re-render and set height on hidden elements
    setCardHeight(cardElement.clientHeight);

    // Keep button/background heights in sync if card content changes size (e.g. text wraps to more rows)
    const resizeObserver = new ResizeObserver(() => {
      setCardHeight(cardElement.clientHeight);
    });
    resizeObserver.observe(cardElement);

    return () => resizeObserver.disconnect();
  }, []);

  // When there's just one button, use half the width
  const BUTTON_VISIBILITY_THRESHOLD_PX = !secondaryButtonProps ? 75 : 150;

  function resetSlide(animated = false) {
    moveXCoords.current = { start: null, current: null, isDragging: false, isOpen: false };
    onOpenStateChange?.(false);
    if (animated) {
      const SNAP_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      const SNAP_DURATION = '0.3s';
      cardRef.current!.style.transition = `transform ${SNAP_DURATION} ${SNAP_EASING}, border-radius ${SNAP_DURATION} ${SNAP_EASING}`;
      buttonSetContainerRef.current!.style.transition = `all ${SNAP_DURATION} ${SNAP_EASING}`;
      redBgRef.current!.style.transition = 'opacity 0.3s ease';
      greyBgRef.current!.style.transition = 'opacity 0.3s ease';
      redBgRef.current!.style.opacity = '0';
      greyBgRef.current!.style.opacity = '0';
      cardRef.current!.style.transform = `translateX(0px)`;
      cardRef.current!.style.borderTopRightRadius = '5px';
      cardRef.current!.style.borderBottomRightRadius = '22px';
      buttonSetContainerRef.current!.style.width = '0px';
      // Clean up transitions after animation completes
      setTimeout(() => {
        if (cardRef.current) cardRef.current.style.transition = '';
        if (buttonSetContainerRef.current) {
          buttonSetContainerRef.current.style.transition = '';
          // Hide only after the snap-closed animation completes so the buttons slide out rather than vanish
          buttonSetContainerRef.current.style.visibility = 'hidden';
        }
        if (redBgRef.current) {
          redBgRef.current.style.visibility = 'hidden';
          redBgRef.current.style.opacity = '';
          redBgRef.current.style.transition = '';
        }
        if (greyBgRef.current) {
          greyBgRef.current.style.visibility = 'hidden';
          greyBgRef.current.style.opacity = '';
          greyBgRef.current.style.transition = '';
        }
      }, 320);
    } else {
      cardRef.current!.style.transform = `translateX(0px)`;
      cardRef.current!.style.borderTopRightRadius = '5px';
      cardRef.current!.style.borderBottomRightRadius = '22px';
      buttonSetContainerRef.current!.style.width = '0px';
      buttonSetContainerRef.current!.style.visibility = 'hidden';
      redBgRef.current!.style.visibility = 'hidden';
      greyBgRef.current!.style.visibility = 'hidden';
    }
  }

  // Replays the swipe hint when the card body is tapped (not swiped). Opt-in via hintOnTap; any consumer
  // onClick still fires. Skipped during E2E so the hint can't race swipe assertions.
  function handleClick(event: MouseEvent<HTMLDivElement>) {
    if (hintOnTap && !disableSlide && !wasDraggedRef.current && !moveXCoords.current.isOpen && !isRunningInCypress()) {
      playSwipeHint();
    }
    wasDraggedRef.current = false;
    onClick?.(event);
  }

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    if (disableSlide) return;

    wasDraggedRef.current = false;
    // Cancel any in-flight hint animation timers to prevent them from interfering with the swipe gesture.
    // This prevents the hint cleanup from setting visibility: 'hidden' on backgrounds mid-swipe.
    hintTimersRef.current.forEach(clearTimeout);
    hintTimersRef.current.length = 0;
    isHintPlayingRef.current = false;

    const touch = event.touches[0];
    moveXCoords.current = {
      start: touch.pageX,
      current: touch.pageX,
      isDragging: true,
      isOpen: moveXCoords.current.isOpen,
    };
  }

  function handleTouchEnd() {
    if (disableSlide) return;

    const distance = moveXCoords.current.start! - moveXCoords.current.current!;
    const SNAP_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    const SNAP_DURATION = '0.3s';

    if (distance >= BUTTON_VISIBILITY_THRESHOLD_PX) {
      moveXCoords.current = { start: null, current: null, isDragging: false, isOpen: true };
      onOpenStateChange?.(true);
      // Smooth snap-to-open with border radius animation
      cardRef.current!.style.transition = `transform ${SNAP_DURATION} ${SNAP_EASING}, border-radius ${SNAP_DURATION} ${SNAP_EASING}`;
      buttonSetContainerRef.current!.style.transition = `all ${SNAP_DURATION} ${SNAP_EASING}`;
      cardRef.current!.style.transform = `translateX(-${BUTTON_VISIBILITY_THRESHOLD_PX}px)`;
      // Keep the card rounded; the red background shows through the corner notch behind it
      buttonSetContainerRef.current!.style.visibility = 'visible';
      buttonSetContainerRef.current!.style.width = `${BUTTON_VISIBILITY_THRESHOLD_PX}px`;
      setTimeout(() => {
        if (cardRef.current) cardRef.current.style.transition = '';
        if (buttonSetContainerRef.current) buttonSetContainerRef.current.style.transition = '';
      }, 320);
    } else if (wasDraggedRef.current || moveXCoords.current.isOpen) {
      // Snap back after a partial drag, or close an open card. When tapping to close an open card, mark the
      // gesture as a drag so the trailing click doesn't replay the swipe hint immediately after it closes.
      if (moveXCoords.current.isOpen && !wasDraggedRef.current) wasDraggedRef.current = true;
      resetSlide(true);
    } else {
      // A pure tap on a closed card: nothing to reset. Running the reset animation here would fade and hide
      // the action backgrounds, stomping the tap-triggered swipe hint mid-peek (grey flashing to white).
      moveXCoords.current = { start: null, current: null, isDragging: false, isOpen: false };
    }
  }

  function handleTouchMove(event: TouchEvent<HTMLDivElement>) {
    if (!moveXCoords.current.isDragging || disableSlide) return;

    const touch = event.touches[0];
    moveXCoords.current = {
      start: moveXCoords.current.start,
      current: touch.pageX,
      isDragging: true,
      isOpen: moveXCoords.current.isOpen,
    };

    const xDiff = moveXCoords.current.start! - touch.pageX;

    // Past the threshold the gesture is a swipe, not a tap — so a trailing click won't replay the hint.
    if (Math.abs(xDiff) >= MINIMUM_INITIAL_DRAG_THRESHOLD_PX) wasDraggedRef.current = true;

    if (!moveXCoords.current.isOpen && xDiff > 0 && xDiff <= BUTTON_VISIBILITY_THRESHOLD_PX) {
      if (xDiff < MINIMUM_INITIAL_DRAG_THRESHOLD_PX) return;

      // Remove any lingering snap transition during active drag
      cardRef.current!.style.transition = '';
      buttonSetContainerRef.current!.style.transition = '';

      redBgRef.current!.style.visibility = 'visible';
      greyBgRef.current!.style.visibility = 'visible';
      // Keep the segments fully opaque so the corner notch shows solid colour with a sharp grey/red
      // boundary, instead of a translucent fade that reads as a gradient while dragging
      redBgRef.current!.style.opacity = '1';
      greyBgRef.current!.style.opacity = '1';
      buttonSetContainerRef.current!.style.visibility = 'visible';
      // Card keeps its rounded corners; the grey/red backgrounds show through the corner notch behind it
      cardRef.current!.style.transform = `translateX(${xDiff * -1}px)`;
      buttonSetContainerRef.current!.style.width = `${xDiff}px`;
    } else if (moveXCoords.current.isOpen && xDiff < 0) {
      // Remove transition during active drag back
      cardRef.current!.style.transition = '';
      buttonSetContainerRef.current!.style.transition = '';

      if (xDiff * -1 > BUTTON_VISIBILITY_THRESHOLD_PX) {
        cardRef.current!.style.transform = `translateX(0px)`;
        buttonSetContainerRef.current!.style.width = `0px`;
      } else {
        cardRef.current!.style.transform = `translateX(${xDiff * -1 - BUTTON_VISIBILITY_THRESHOLD_PX}px)`;
        buttonSetContainerRef.current!.style.width = `${BUTTON_VISIBILITY_THRESHOLD_PX - xDiff * -1}px`;
      }
    }
  }

  return (
    <Flex {...containerProps} pos="relative">
      <SEHomeCard
        zIndex={6}
        ref={cardRef}
        position="relative"
        overflow="hidden"
        // Claim horizontal gestures for the slide-to-action swipe so the browser only handles vertical scrolling
        sx={{ touchAction: 'pan-y' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onClick={handleClick}
        borderTopRightRadius="5px"
        // Match the primary action button's bottom-right radius so the card's corner nests flush
        // with the "card below" revealed during the swipe, rather than protruding past it
        borderBottomRightRadius="22px"
        {...cardProps}
      >
        {children}
      </SEHomeCard>
      {/* These backgrounds fill the corner notch behind the rounded card as it slides. They mirror the
          action buttons exactly — red behind delete (left half), grey behind archive (right half) — with the
          red segment stacked above the grey one so its rounded crease reveals grey through the corner notch
          mid-swipe, just like the delete/archive buttons that slide in afterwards. */}
      <Box
        ref={redBgRef}
        // Above the grey segment so the red card's rounded crease reveals grey behind it (matches the buttons)
        zIndex={4}
        visibility={'hidden'}
        opacity={0}
        bg={'#DC0A0A'}
        height={`${cardHeight}px`}
        right={secondaryButtonProps ? `${BUTTON_VISIBILITY_THRESHOLD_PX / 2}px` : 0}
        // Delete-only: extend the red segment 22px past the button area so it sits *behind* the card's rounded
        // bottom-right corner. Without this overlap the corner notch falls just left of the red segment and
        // reveals the page background (the white corner) once the card is fully swiped open. 22px == the card's
        // corner radius. The two-button case relies on the grey segment's matching overlap instead.
        w={secondaryButtonProps ? `${BUTTON_VISIBILITY_THRESHOLD_PX}px` : `${BUTTON_VISIBILITY_THRESHOLD_PX + 22}px`}
        pos={'absolute'}
        boxShadow={secondaryButtonProps ? '6px 0 12px -2px rgba(0, 0, 0, 0.35)' : undefined}
        borderTopRightRadius={'5px'}
        // The red card has its own rounded crease (22px) so grey peeks through mid-swipe; when there's no
        // archive button the red segment owns the full outer corner — which is the card's own 22px corner
        // (see the card's borderBottomRightRadius above), not SEHomeCard's default 25px. Matching it avoids a
        // transparent crescent showing through behind the delete button's corner when fully swiped.
        borderBottomRightRadius={'22px'}
      />
      <Box
        ref={greyBgRef}
        zIndex={3}
        visibility={'hidden'}
        opacity={0}
        bg={'#5C6466'}
        height={`${cardHeight}px`}
        right={0}
        // Extend left under the red segment's rounded crease (mirroring the archive button's overlap) so the
        // grey card shows through the corner notch as the card is swiped
        w={secondaryButtonProps ? `${BUTTON_VISIBILITY_THRESHOLD_PX / 2 + 22}px` : '0px'}
        pos={'absolute'}
        borderTopRightRadius={'5px'}
        borderBottomRightRadius={'25px'}
      />
      {/* The menu itself — fixed internal width, revealed via overflow clip */}
      <Box
        zIndex={5}
        height={`${cardHeight}px`}
        overflow={'hidden'}
        width={'0px'}
        visibility={'hidden'}
        right={0}
        position={'absolute'}
        ref={buttonSetContainerRef}
      >
        <Flex height={'100%'} w={`${BUTTON_VISIBILITY_THRESHOLD_PX}px`} position="absolute" right={0} top={0}>
          <Center
            as={'button'}
            flexDirection={'column'}
            position="relative"
            // Sit above the archive button so the rounded crease reveals the grey card behind it
            zIndex={1}
            borderTopRightRadius={'5px'}
            borderBottomRightRadius={'22px'}
            // Soft shadow off the rounded crease sells the "two stacked cards" look
            boxShadow={secondaryButtonProps ? '6px 0 12px -2px rgba(0, 0, 0, 0.35)' : undefined}
            h="100%"
            w={secondaryButtonProps ? `${BUTTON_VISIBILITY_THRESHOLD_PX / 2}px` : '100%'}
            bg={'#DC0A0A'}
            color="#fff"
            {...primaryButtonProps}
            onClick={(e) => {
              primaryButtonProps.onClick && primaryButtonProps.onClick(e);
            }}
          >
            {primaryButtonProps.children}
          </Center>
          {secondaryButtonProps && (
            <Center
              as={'button'}
              flexDirection={'column'}
              position="relative"
              zIndex={0}
              // Extend left under the red card's rounded crease so its corner peeks through,
              // while keeping the visible (non-overlapped) area equal to the delete button.
              // pl re-centres the icon within that visible area.
              ml={'-22px'}
              pl={'22px'}
              borderTopRightRadius={'5px'}
              borderBottomRightRadius={'25px'}
              h="100%"
              w={`${BUTTON_VISIBILITY_THRESHOLD_PX / 2 + 22}px`}
              bg={'#5C6466'}
              color="#fff"
              {...secondaryButtonProps}
              onClick={(e) => {
                secondaryButtonProps.onClick && secondaryButtonProps.onClick(e);
              }}
            >
              {secondaryButtonProps.children}
            </Center>
          )}
        </Flex>
      </Box>
    </Flex>
  );
}
